import React from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Header from "../../components/Header";
import DashboardSidebar from "../../components/DashboardSidebar";
import { FiCreditCard, FiArrowRight, FiDownload, FiExternalLink } from "react-icons/fi";
import "../App/App.css";
import "./Dashboard.css";

function getApiBase(): string {
    return (process.env.NODE_ENV === "production" &&
        (!process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL.includes("localhost")))
        ? "https://interview-sandbox-production.up.railway.app"
        : process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
}

interface CreditsPageProps {
    darkMode: boolean;
}

interface Pack {
    id: string;
    name: string;
    credits: number;
    price: number;
    priceCents: number;
}

interface Transaction {
    id: string;
    type: string;
    amount: number;
    balance_after: number;
    description: string;
    created_at: string;
    reference_id?: string;
    interviewId?: string;
    sessionId?: string;
    interviewTitle?: string;
    candidateName?: string;
    candidateScore?: number;
}

interface Purchase {
    id: string;
    pack_name: string;
    credits: number;
    amount_cents: number;
    status: string;
    created_at: string;
    receipt_url?: string;
    stripe_session_id?: string;
}

export default function CreditsPage({ darkMode }: CreditsPageProps): React.ReactElement {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [user, setUser] = React.useState<{ uid: string; email?: string } | null>(null);
    const [accountType, setAccountType] = React.useState<string | null>(null);
    const [companyName, setCompanyName] = React.useState<string | null>(null);
    const [balance, setBalance] = React.useState<number | null>(null);
    const [creditsPurchasedTotal, setCreditsPurchasedTotal] = React.useState<number>(0);
    const [packs, setPacks] = React.useState<Pack[]>([]);
    const [transactions, setTransactions] = React.useState<Transaction[]>([]);
    const [purchases, setPurchases] = React.useState<Purchase[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [buyingPack, setBuyingPack] = React.useState<string | null>(null);
    const [downloadingReceipt, setDownloadingReceipt] = React.useState<string | null>(null);

    // Reset "Redirecting..." when user returns from Stripe without paying (back button, closed tab)
    React.useEffect(() => {
        const handlePageShow = (e: PageTransitionEvent) => {
            if (e.persisted) setBuyingPack(null);
        };
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") setBuyingPack(null);
        };
        window.addEventListener("pageshow", handlePageShow);
        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => {
            window.removeEventListener("pageshow", handlePageShow);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);

    React.useEffect(() => {
        const auth = getAuth();
        const unsub = onAuthStateChanged(auth, (u) => {
            if (!u) {
                navigate("/login");
                return;
            }
            setUser({ uid: u.uid, email: u.email || undefined });
        });
        return unsub;
    }, [navigate]);

    React.useEffect(() => {
        if (!user) return;

        const loadUserData = async () => {
            const { getDatabase, ref, get, child } = await import("firebase/database");
            const db = getDatabase();
            const snap = await get(child(ref(db), `users/${user.uid}`));
            if (snap.exists()) {
                const d = snap.val();
                setAccountType(d.accountType || "job_seeker");
                setCompanyName(d.companyName || null);
            } else {
                setAccountType("job_seeker");
            }
        };
        loadUserData();
    }, [user]);

    // Refetch balance when returning from Stripe success
    const stripeSuccess = searchParams.get("success");
    React.useEffect(() => {
        if (stripeSuccess && user && accountType === "company") {
            getAuth()
                .currentUser?.getIdToken()
                .then((token) => {
                    if (token) {
                        return fetch(`${getApiBase()}/api/credits/balance`, {
                            headers: { Authorization: `Bearer ${token}` },
                        })
                            .then((r) => r.json())
                            .then((b) => {
                                setBalance(b.balance);
                                setCreditsPurchasedTotal(b.credits_purchased_total ?? 0);
                            });
                    }
                })
                .catch(() => {})
                .finally(() => setSearchParams({}, { replace: true }));
        } else if (searchParams.get("canceled")) {
            setSearchParams({}, { replace: true });
        }
    }, [stripeSuccess, user, accountType, setSearchParams]);

    React.useEffect(() => {
        if (!user || accountType === null) return;
        if (accountType !== "company") {
            setLoading(false);
            return;
        }

        const load = async () => {
            try {
                const token = await getAuth().currentUser?.getIdToken();
                const base = getApiBase();
                const authHeaders = { Authorization: `Bearer ${token}` };
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 15000);

                const [balanceRes, packsRes, txRes, purchasesRes] = await Promise.all([
                    fetch(`${base}/api/credits/balance`, { headers: authHeaders, signal: controller.signal }),
                    fetch(`${base}/api/credits/packs`, { signal: controller.signal }),
                    fetch(`${base}/api/credits/transactions`, { headers: authHeaders, signal: controller.signal }),
                    fetch(`${base}/api/credits/purchases`, { headers: authHeaders, signal: controller.signal }),
                ]);
                clearTimeout(timeoutId);

                if (balanceRes.ok) {
                    const b = await balanceRes.json();
                    setBalance(b.balance);
                    setCreditsPurchasedTotal(b.credits_purchased_total ?? 0);
                }
                if (packsRes.ok) {
                    const p = await packsRes.json();
                    setPacks(p.packs || []);
                }
                if (txRes.ok) {
                    const t = await txRes.json();
                    setTransactions(t.transactions || []);
                }
                if (purchasesRes.ok) {
                    const pu = await purchasesRes.json();
                    setPurchases(pu.purchases || []);
                }
            } catch (e) {
                console.error("Failed to load credits data:", e);
                setBalance(0);
                setTransactions([]);
                setPurchases([]);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [user, accountType]);

    const handleBuyCredits = async (packId: string) => {
        const token = await getAuth().currentUser?.getIdToken();
        if (!token) return;
        setBuyingPack(packId);
        try {
            const res = await fetch(`${getApiBase()}/api/stripe/create-checkout-session`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ pack: packId }),
            });
            const text = await res.text();
            let data: { url?: string; error?: string; message?: string } = {};
            try {
                data = JSON.parse(text);
            } catch {
                console.error("Checkout failed:", res.status, text.slice(0, 200));
                alert(`Checkout failed (${res.status}). Ensure STRIPE_SECRET_KEY is set in .env and restart the server.`);
                setBuyingPack(null);
                return;
            }
            if (data.url) {
                window.location.href = data.url;
            } else {
                alert(data.error || data.message || "Failed to create checkout");
                setBuyingPack(null);
            }
        } catch (e) {
            console.error("Failed to create checkout:", e);
            alert("Failed to create checkout. Check the console for details.");
            setBuyingPack(null);
        }
    };

    const handleDownloadReceipt = async (purchaseId: string, format: "pdf" | "url") => {
        const token = await getAuth().currentUser?.getIdToken();
        if (!token) return;
        setDownloadingReceipt(purchaseId);
        try {
            const url = `${getApiBase()}/api/credits/receipt/${purchaseId}?format=${format}`;
            if (format === "url") {
                window.open(url, "_blank");
                setDownloadingReceipt(null);
                return;
            }
            const res = await fetch(url, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Failed to load receipt");
            const blob = await res.blob();
            const blobUrl = URL.createObjectURL(blob);
            window.open(blobUrl, "_blank");
            setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        } catch (e) {
            console.error("Receipt download failed:", e);
            alert("Failed to open receipt.");
        } finally {
            setDownloadingReceipt(null);
        }
    };

    const handleLogout = async () => {
        const { signOut } = await import("firebase/auth");
        await signOut(getAuth());
        navigate("/");
    };

    if (accountType === "job_seeker" && !loading) {
        return (
            <div className={darkMode ? "dashboard-wrapper dashboard-dark" : "dashboard-wrapper"}>
                <DashboardSidebar
                    darkMode={darkMode}
                    accountType="job_seeker"
                    companyName={companyName || undefined}
                    onLogout={handleLogout}
                />
                <div className="dashboard-main">
                    <Header darkMode={darkMode} />
                    <div className="dashboard">
                        <div className={`credits-non-company ${darkMode ? "credits-non-company-dark" : ""}`}>
                            <FiCreditCard size={32} />
                            <h3>Credits are for company accounts</h3>
                            <p>Purchase interview credits to run AI interviews. Switch to a company account or <Link to="/dashboard">go to Dashboard</Link>.</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={darkMode ? "dashboard-wrapper dashboard-dark" : "dashboard-wrapper"}>
            <DashboardSidebar
                darkMode={darkMode}
                accountType={(accountType as "company" | "job_seeker") || "job_seeker"}
                companyName={companyName || undefined}
                onLogout={handleLogout}
            />
            <main className="dashboard-main">
                <Header darkMode={darkMode} />
                <div className="dashboard">
                    {loading ? (
                        <div className={`credits-loading ${darkMode ? "credits-loading-dark" : ""}`}>
                            <span className="credits-loading-spinner" />
                            <p>Loading credits…</p>
                        </div>
                    ) : (
                        <>
                            {/* Zero balance banner */}
                            {balance === 0 && (
                                <div
                                    className={`credits-banner credits-banner-zero ${darkMode ? "credits-banner-dark" : ""}`}
                                >
                                    <FiCreditCard size={24} />
                                    <span>You're out of credits. Buy more to keep interviewing.</span>
                                </div>
                            )}

                            {/* Balance card */}
                            <div className={`credits-balance-card ${darkMode ? "credits-balance-card-dark" : ""}`}>
                                <div className="credits-balance-header">
                                    <h2>Credits</h2>
                                    <div className="credits-balance-value">
                                        <span className="credits-number">{balance ?? 0}</span>
                                        <span className="credits-label">remaining</span>
                                    </div>
                                </div>
                                <p className="credits-balance-sub">
                                    {creditsPurchasedTotal > 0
                                        ? `${creditsPurchasedTotal} credits purchased total`
                                        : "1 credit = 1 completed AI interview"}
                                </p>
                            </div>

                            {/* Buy credits */}
                            <section className="credits-section">
                                <h3>Buy Credits</h3>
                                <div className="credits-packs-grid">
                                    {packs.map((pack) => (
                                        <div
                                            key={pack.id}
                                            className={`credits-pack-card ${darkMode ? "credits-pack-card-dark" : ""}`}
                                        >
                                            <div className="credits-pack-name">{pack.name}</div>
                                            <div className="credits-pack-credits">{pack.credits} credits</div>
                                            <div className="credits-pack-price">${pack.price}</div>
                                            <div className="credits-pack-per">
                                                ${(pack.price / pack.credits).toFixed(2)}/interview
                                            </div>
                                            <button
                                                className={`credits-pack-btn ${darkMode ? "credits-pack-btn-dark" : ""}`}
                                                onClick={() => handleBuyCredits(pack.id)}
                                                disabled={!!buyingPack}
                                            >
                                                {buyingPack === pack.id ? (
                                                    "Redirecting…"
                                                ) : (
                                                    <>
                                                        Buy <FiArrowRight size={14} />
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </section>

                            {/* Transaction log */}
                            <section className="credits-section">
                                <h3>Transaction History</h3>
                                {transactions.length === 0 ? (
                                    <p className="credits-empty">No transactions yet.</p>
                                ) : (
                                    <div className={`credits-table-wrap ${darkMode ? "credits-table-wrap-dark" : ""}`}>
                                        <table className="credits-table">
                                            <thead>
                                                <tr>
                                                    <th>Date</th>
                                                    <th>Type</th>
                                                    <th>Amount</th>
                                                    <th>Balance After</th>
                                                    <th>Description</th>
                                                    <th>Report</th>
                                                    <th>Receipt</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {transactions.map((tx) => {
                                                    const purchase = tx.type === "purchase" && tx.reference_id
                                                        ? purchases.find((p) => p.stripe_session_id === tx.reference_id)
                                                        : null;
                                                    const desc = tx.type === "consumption" && (tx.interviewTitle || tx.candidateName)
                                                        ? `${tx.interviewTitle || "Interview"}${tx.candidateName ? ` — ${tx.candidateName}` : ""}${tx.candidateScore != null ? ` (${tx.candidateScore})` : ""}`
                                                        : tx.description;
                                                    return (
                                                        <tr key={tx.id}>
                                                            <td>{new Date(tx.created_at).toLocaleString()}</td>
                                                            <td>{tx.type}</td>
                                                            <td>{tx.amount > 0 ? `+${tx.amount}` : tx.amount}</td>
                                                            <td>{tx.balance_after}</td>
                                                            <td>{desc}</td>
                                                            <td>
                                                                {tx.type === "consumption" && tx.interviewId ? (
                                                                    <button
                                                                        type="button"
                                                                        className="credits-receipt-btn"
                                                                        onClick={() => navigate(`/company/results/${tx.interviewId}`)}
                                                                        title="View candidate report"
                                                                    >
                                                                        <FiExternalLink size={14} /> Report
                                                                    </button>
                                                                ) : (
                                                                    "—"
                                                                )}
                                                            </td>
                                                            <td>
                                                                {purchase ? (
                                                                    <span className="credits-receipt-links">
                                                                        <button
                                                                            type="button"
                                                                            className="credits-receipt-btn"
                                                                            onClick={() => handleDownloadReceipt(purchase.id, "pdf")}
                                                                            disabled={!!downloadingReceipt}
                                                                            title="View receipt in new tab"
                                                                        >
                                                                            {downloadingReceipt === purchase.id ? "…" : <><FiDownload size={14} /> PDF</>}
                                                                        </button>
                                                                        {purchase.receipt_url && (
                                                                            <>
                                                                                {" · "}
                                                                                <a href={purchase.receipt_url} target="_blank" rel="noopener noreferrer" className="credits-receipt-link" title="View Stripe receipt">
                                                                                    <FiExternalLink size={14} /> Stripe
                                                                                </a>
                                                                            </>
                                                                        )}
                                                                    </span>
                                                                ) : (
                                                                    "—"
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </section>

                            {/* Purchase history */}
                            <section className="credits-section">
                                <h3>Purchase History</h3>
                                {purchases.length === 0 ? (
                                    <p className="credits-empty">No purchases yet.</p>
                                ) : (
                                    <div className={`credits-table-wrap ${darkMode ? "credits-table-wrap-dark" : ""}`}>
                                        <table className="credits-table">
                                            <thead>
                                                <tr>
                                                    <th>Date</th>
                                                    <th>Pack</th>
                                                    <th>Credits</th>
                                                    <th>Amount</th>
                                                    <th>Status</th>
                                                    <th>Receipt</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {purchases.map((p) => (
                                                    <tr key={p.id}>
                                                        <td>{new Date(p.created_at).toLocaleString()}</td>
                                                        <td>{p.pack_name}</td>
                                                        <td>{p.credits}</td>
                                                        <td>${(p.amount_cents / 100).toFixed(2)}</td>
                                                        <td>{p.status}</td>
                                                        <td>
                                                            <span className="credits-receipt-links">
                                                                <button
                                                                    type="button"
                                                                    className="credits-receipt-btn"
                                                                    onClick={() => handleDownloadReceipt(p.id, "pdf")}
                                                                    disabled={!!downloadingReceipt}
                                                                    title="View receipt in new tab"
                                                                >
                                                                    {downloadingReceipt === p.id ? (
                                                                        "…"
                                                                    ) : (
                                                                        <><FiDownload size={14} /> PDF</>
                                                                    )}
                                                                </button>
                                                                {p.receipt_url && (
                                                                    <>
                                                                        {" · "}
                                                                        <a
                                                                            href={p.receipt_url}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="credits-receipt-link"
                                                                            title="View Stripe receipt"
                                                                        >
                                                                            <FiExternalLink size={14} /> Stripe
                                                                        </a>
                                                                    </>
                                                                )}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </section>
                        </>
                    )}
                </div>
            </main>
        </div>
    );
}
